import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Rocket, X, Check, Wallet, BadgeCheck, TrendingUp, Clock, AlertTriangle } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useTipWallet } from '../hooks/useTipWallet';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useNostrQuery } from '../hooks/useNostrQuery';
import { useTrustScore } from '../hooks/useTrustScore';
import { canPurchaseBoost } from '../lib/trustScore';
import {
  BOOST_WALLET, BOOST_KIND,
  GOAL_BOOST_TAG, GOAL_BOOST_D_TAG_PREFIX, GOAL_BOOST_DURATION,
  GOAL_BOOST_MIN_BID_USD, GOAL_BOOST_BID_PRESETS,
  usdToSats, satsToUsdStr, getActiveGoalBoosts, type GoalBoostData,
} from '../lib/boost';

interface BoostGoalModalProps {
  /** The creator whose goal is being promoted. */
  creatorPubkey: string;
  /** Short title of the goal, shown for context. */
  goalTitle?: string;
  onClose: () => void;
  onBoostSuccess?: () => void;
}

/**
 * Turn a raw wallet/broadcast error into a plain-language explanation of why
 * the boost didn't go through. Every message reassures the member that no
 * BCH left their wallet (the tx never broadcast) and tells them what to do.
 */
function explainPaymentFailure(err: unknown): string {
  const raw = (err instanceof Error ? err.message : String(err ?? '')).toLowerCase();

  if (raw.includes('insufficient') || raw.includes('not enough') || raw.includes('balance')) {
    return "Payment failed: your tip wallet doesn't have enough BCH to cover this boost. No BCH was charged. Top up your wallet in Settings and try again.";
  }
  if (raw.includes('not ready') || raw.includes('no wallet') || raw.includes('wif')) {
    return 'Payment failed: your BCH wallet isn’t ready yet. No BCH was charged. Load your wallet and try again in a moment.';
  }
  if (raw.includes('mempool') || raw.includes('conflict') || raw.includes('utxo') || raw.includes('double')) {
    return 'Payment failed: a previous transaction is still confirming, so this one couldn’t be sent. No BCH was charged. Wait about a minute and try again.';
  }
  if (raw.includes('dust') || raw.includes('too small')) {
    return 'Payment failed: the boost amount is too small to send on-chain. No BCH was charged. Try a slightly higher bid.';
  }
  if (raw.includes('network') || raw.includes('timeout') || raw.includes('electrum') || raw.includes('connect') || raw.includes('fetch')) {
    return 'Payment failed: couldn’t reach the Bitcoin Cash network. No BCH was charged. Check your connection and try again.';
  }
  if (raw.includes('invalid') && raw.includes('address')) {
    return 'Payment failed: the boost payment address looks invalid, so nothing was sent. No BCH was charged. Please report this to the BCH Nostr team.';
  }
  // Fallback — still make it clear money is safe and the boost isn't live.
  return 'Payment failed, so your boost isn’t live. No BCH was charged. Please check your wallet balance and try again.';
}

/**
 * Pay BCH to draw attention to a creator goal. Anyone (a supporter or the
 * creator) can boost — boosted goals float to the top of the goals list and
 * wear a "Boosted" badge for 6 hours. Payment goes to the platform wallet,
 * exactly like post boosts, and the Nostr event is published BEFORE the BCH
 * is sent so a denied signature never costs money.
 */
export default function BoostGoalModal({ creatorPubkey, goalTitle, onClose, onBoostSuccess }: BoostGoalModalProps) {
  const { user } = useAppContext();
  const tipWallet = useTipWallet();
  const { price: bchPrice } = useBCHPrice();
  const { publish } = useNostrPublish();
  const trust = useTrustScore(user?.pubkey);
  const eligibility = canPurchaseBoost(trust);

  const [bidUsd, setBidUsd] = useState(1);
  const [customInput, setCustomInput] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [boosting, setBoosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [walletInitializing, setWalletInitializing] = useState(false);

  const bidSats = usdToSats(bidUsd, bchPrice);
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = bidSats !== null && walletBalance >= bidSats;
  const bidTooLow = bidUsd < GOAL_BOOST_MIN_BID_USD;

  // Existing active boosts for this goal (so we can show current status).
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 2);
  const { events: goalBoostEvents } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], '#t': [GOAL_BOOST_TAG], since, limit: 100 }],
    enabled: true,
    allRelays: true,
  });
  const existingBoost = useMemo(
    () => getActiveGoalBoosts(goalBoostEvents).get(creatorPubkey) ?? null,
    [goalBoostEvents, creatorPubkey],
  );

  const handlePreset = (usd: number) => { setBidUsd(usd); setShowCustom(false); setCustomInput(''); };
  const handleCustomInput = (val: string) => {
    setCustomInput(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) setBidUsd(Math.round(num * 100) / 100);
  };
  const handleInitWallet = async () => {
    setWalletInitializing(true);
    try { await tipWallet.init(); } catch { /* ignore */ }
    setWalletInitializing(false);
  };

  const handleBoost = async () => {
    if (!user || !canAfford || bidSats === null || bidTooLow || !eligibility.eligible) return;
    setBoosting(true);
    setError(null);

    const now = Math.floor(Date.now() / 1000);
    const expiresAt = now + GOAL_BOOST_DURATION;
    const data: GoalBoostData = {
      creatorPubkey,
      priceSats: bidSats,
      expiresAt,
      boostedBy: user.pubkey,
      bidUsd,
    };

    const tags = [
      ['d', GOAL_BOOST_D_TAG_PREFIX + creatorPubkey],
      ['t', GOAL_BOOST_TAG],
      ['t', 'bch'],
      ['p', creatorPubkey],
      ['expires', String(expiresAt)],
      ['amount', String(bidSats)],
    ];

    // ── Step 1: publish the boost event FIRST (no money at risk yet) ──
    try {
      await publish({ kind: BOOST_KIND, content: JSON.stringify(data), tags, created_at: now });
    } catch (e) {
      setBoosting(false);
      setError(
        e instanceof Error && (e.message.includes('denied') || e.message.includes('reject'))
          ? 'Signing was denied. No BCH was charged.'
          : 'Failed to publish boost. No BCH was charged. Please try again.',
      );
      return;
    }

    // ── Step 2: send BCH ──
    // The boost is only real once the BCH is actually sent — on-chain
    // verification (useVerifiedGoalBoosts) drops any boost whose payment
    // never lands. So a payment failure here is a genuine FAILURE, not a
    // soft success: tell the member exactly why the boost didn't go through.
    try {
      const txId = await tipWallet.sendBCH(BOOST_WALLET, BigInt(bidSats));
      // Re-publish with txid so it can be verified on-chain.
      publish({
        kind: BOOST_KIND,
        content: JSON.stringify(data),
        tags: [...tags, ['txid', txId]],
        created_at: now + 1,
      }).catch(() => {});
      setSuccess(true);
      tipWallet.refreshBalance();
      onBoostSuccess?.();
      window.dispatchEvent(new CustomEvent('bchnostr:goal-boost-published', { detail: { creatorPubkey } }));
    } catch (e) {
      tipWallet.refreshBalance();
      setError(explainPaymentFailure(e));
    } finally {
      setBoosting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl bg-popover border border-border shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <h2 className="font-bold text-base flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Rocket className="w-4 h-4 text-white" />
            </div>
            Boost Goal
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 pb-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {trust.loading ? (
            <div className="text-center py-10 space-y-3">
              <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin mx-auto" />
              <p className="text-xs text-muted-foreground">Checking eligibility...</p>
            </div>
          ) : !eligibility.eligible ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <BadgeCheck className="w-7 h-7 text-primary" />
              </div>
              <p className="text-sm font-bold">Verification Required</p>
              <p className="text-xs text-muted-foreground leading-relaxed px-2">{eligibility.reason}</p>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-muted text-foreground text-sm font-medium hover:bg-muted/80 transition-colors">Got it</button>
            </div>
          ) : success ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-emerald-500" />
              </div>
              <p className="text-sm font-bold text-emerald-500">Goal Boosted!</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                This goal will be featured at the top of Creator Support for the next 6 hours.
              </p>
              {error && <p className="text-[11px] text-amber-600 dark:text-amber-400">{error}</p>}
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">Done</button>
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Draw attention to {goalTitle ? <strong className="text-foreground">"{goalTitle}"</strong> : 'this goal'}. Higher
                bids rank higher among boosted goals. Boosts last <strong className="text-foreground">6 hours</strong>.
              </p>

              {existingBoost && (
                <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 px-3 py-2.5">
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1.5">
                    <Rocket className="w-3 h-3" />
                    Currently boosted at {satsToUsdStr(existingBoost.priceSats, bchPrice) || `${existingBoost.priceSats.toLocaleString()} sats`}.
                    Bid higher to take the top spot.
                  </p>
                </div>
              )}

              {/* Bid */}
              <div className="space-y-3">
                <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-primary" /> Your Bid
                </label>
                <div className="flex gap-1.5 flex-wrap">
                  {GOAL_BOOST_BID_PRESETS.map(p => (
                    <button
                      key={p.usd}
                      onClick={() => handlePreset(p.usd)}
                      className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                        bidUsd === p.usd && !showCustom
                          ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary/30'
                          : 'border-border hover:border-primary/40 text-foreground'
                      }`}
                    >
                      <span>{p.emoji}</span><span>{p.label}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => setShowCustom(!showCustom)}
                    className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                      showCustom ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary/30' : 'border-border hover:border-primary/40 text-muted-foreground'
                    }`}
                  >
                    Custom
                  </button>
                </div>

                {showCustom && (
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-bold">$</span>
                    <input
                      type="number" min={GOAL_BOOST_MIN_BID_USD} step="0.05"
                      value={customInput}
                      onChange={e => handleCustomInput(e.target.value)}
                      placeholder={`Min $${GOAL_BOOST_MIN_BID_USD.toFixed(2)}`}
                      className="w-full pl-7 pr-3 py-2.5 rounded-xl border border-border bg-muted/50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                      autoFocus
                    />
                  </div>
                )}

                {/* Summary */}
                <div className="rounded-xl bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Your bid</span>
                    <div className="text-right">
                      <span className="text-sm font-bold text-foreground">${bidUsd.toFixed(2)}</span>
                      <span className="text-[10px] text-muted-foreground font-mono ml-1.5">
                        {bidSats ? `~${bidSats.toLocaleString()} sats` : '...'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Duration</span>
                    <span className="text-xs font-semibold text-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> 6 hours</span>
                  </div>
                  {bidTooLow && (
                    <p className="text-[10px] text-red-500 font-medium">Minimum bid is ${GOAL_BOOST_MIN_BID_USD.toFixed(2)}</p>
                  )}
                </div>
              </div>

              {/* Wallet */}
              {!tipWallet.ready ? (
                <div className="rounded-xl bg-muted/50 border border-border p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-2">Initialize your wallet to boost</p>
                  <button
                    onClick={handleInitWallet}
                    disabled={walletInitializing}
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1.5 mx-auto"
                  >
                    {walletInitializing ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Wallet className="w-3.5 h-3.5" />}
                    {walletInitializing ? 'Loading...' : 'Load Wallet'}
                  </button>
                </div>
              ) : (
                <div className="rounded-xl bg-muted/50 border border-border p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Wallet Balance</span>
                    <span className={`text-xs font-mono font-bold ${canAfford ? 'text-emerald-500' : 'text-red-500'}`}>
                      {satsToUsdStr(walletBalance, bchPrice) || `${walletBalance.toLocaleString()} sats`}
                    </span>
                  </div>
                  {!canAfford && bidSats !== null && (
                    <p className="text-[10px] text-red-500 mt-1">
                      Not enough funds. Need {satsToUsdStr(bidSats - walletBalance, bchPrice) || `${(bidSats - walletBalance).toLocaleString()} sats`} more.
                      Deposit to your tip wallet in Settings.
                    </p>
                  )}
                </div>
              )}

              <button
                onClick={handleBoost}
                disabled={!tipWallet.ready || !canAfford || boosting || bidTooLow || !eligibility.eligible}
                className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                  tipWallet.ready && canAfford && !bidTooLow
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:opacity-90 active:scale-[0.98]'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                } disabled:opacity-50`}
              >
                {boosting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Rocket className="w-4 h-4" />}
                {boosting ? 'Boosting...' : `Boost for $${bidUsd.toFixed(2)}`}
              </button>

              {error && (
                <div className="flex items-start gap-2 rounded-xl bg-red-500/10 border border-red-500/30 px-3 py-2.5">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-600 dark:text-red-400 leading-relaxed">{error}</p>
                </div>
              )}

              <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
                Boosted goals are featured at the top of Creator Support. Payment supports the platform.
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
